<script>
import {mapGetters} from "vuex";
import MobileMappingInfo from "@/components/ui/mobile-mapping/MobileMappingInfo.vue";
import MobileMappingQuote from "@/components/ui/mobile-mapping/MobileMappingQuote.vue";
import MobileMappingFaq from "@/components/ui/mobile-mapping/MobileMappingFaq.vue";
import MobileMappingContact from "@/components/ui/mobile-mapping/MobileMappingContact.vue";
import MobileMappingSignupJourney from "@/components/ui/mobile-mapping/MobileMappingSignupJourney.vue";
import MobileMappingSignupForm from "@/components/ui/mobile-mapping/MobileMappingSignupForm.vue";
import Breadcrumbs from "@/components/ui/Breadcrumbs.vue";

export default {
  name: "MobileMapping",
  components: {
    Breadcrumbs,
    MobileMappingSignupForm,
    MobileMappingSignupJourney, MobileMappingContact, MobileMappingFaq, MobileMappingQuote, MobileMappingInfo},
  data: () => ({
    initialScrollY: 0,
    restoreTimer: null,
    guardTimer: null,
    scrollGuardActive: false,
    lastUserInputTs: 0,
    mobileMappingHeader: require("@/assets/mobile-mapping-header.jpg"),
    selectedCommune: null,
  }),
  watch: {
    communeMobileMappings: {
      immediate: true,
      handler(communes) {
        if (!this.selectedCommune && communes && communes.length) {
          this.selectedCommune = communes[0].name;
        }
      }
    }
  },
  mounted() {
    this.initialScrollY = window.scrollY || 0;
    window.addEventListener("scroll", this.onWindowScroll, { passive: true });
    window.addEventListener("touchstart", this.onUserInput, { passive: true });
    window.addEventListener("wheel", this.onUserInput, { passive: true });
    window.addEventListener("keydown", this.onUserInput);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onWindowScroll);
    window.removeEventListener("touchstart", this.onUserInput);
    window.removeEventListener("wheel", this.onUserInput);
    window.removeEventListener("keydown", this.onUserInput);

    if (this.restoreTimer) {
      window.clearTimeout(this.restoreTimer);
      this.restoreTimer = null;
    }

    if (this.guardTimer) {
      window.clearTimeout(this.guardTimer);
      this.guardTimer = null;
    }
  },
  computed: {
    ...mapGetters('user', ["mobileMappingUrls", "communeMobileMappings", "isNewEmployee"]),
    selectedIframeUrls() {
      if(!this.isNewEmployee || !this.selectedCommune)  return this.mobileMappingUrls;

      return this.communeMobileMappings.find(c => this.selectedCommune === c.name);
    }
  },
  methods: {
    scrollToForm() {
      const element = document.getElementById("mobile-mapping-signup-form");

      window.scroll({
        top: element.offsetTop,
        left: 0,
        behavior: "smooth"
      });
    },
    onUserInput() {
      this.lastUserInputTs = Date.now();
    },
    onWindowScroll() {
      if (!this.scrollGuardActive) {
        return;
      }

      const isLikelyAutoScroll = Date.now() - this.lastUserInputTs > 250;
      if (isLikelyAutoScroll && Math.abs(window.scrollY - this.initialScrollY) > 2) {
        window.scrollTo(0, this.initialScrollY);
      }
    },
    restoreScrollPosition() {
      window.scrollTo(0, this.initialScrollY);
    },
    onIframeLoad() {
      const iframe = this.$refs.mobileMappingIframe;

      this.initialScrollY = window.scrollY || this.initialScrollY;

      if (iframe && document.activeElement === iframe) {
        iframe.blur();
      }

      this.scrollGuardActive = true;
      window.requestAnimationFrame(this.restoreScrollPosition);

      if (this.restoreTimer) {
        window.clearTimeout(this.restoreTimer);
      }

      this.restoreTimer = window.setTimeout(this.restoreScrollPosition, 200);

      if (this.guardTimer) {
        window.clearTimeout(this.guardTimer);
      }

      this.guardTimer = window.setTimeout(() => {
        this.scrollGuardActive = false;
      }, 1200);
    },
    onIframeFocus() {
      const iframe = this.$refs.mobileMappingIframe;
      if (iframe) {
        iframe.blur();
      }
      this.restoreScrollPosition();
    }
  }
}
</script>

<template>
<div class="new-wrapper min-height-100">
    <div class="pt-10 px-5 py-2">
      <breadcrumbs />
      <template v-if="mobileMappingUrls.iframe || isNewEmployee">
        <div class="d-flex flex-row flex-wrap justify-space-between mb-5" style="align-items: center">
          <span class="h1 new-font-medium">Mobile Mapping</span>
          <v-select v-if="isNewEmployee" v-model="selectedCommune" outlined dense hide-details single-line style="max-width: 227px" :items="communeMobileMappings" item-text="name" />
        </div>

      <div class="iframe-shell">
      <iframe
          v-if="selectedIframeUrls.iframe"
          ref="mobileMappingIframe"
          :src="selectedIframeUrls.iframe"
          tabindex="-1"
          style="width: 100%; aspect-ratio: 1.654676259"
          @load="onIframeLoad"
          @focus="onIframeFocus"
          allow="fullscreen" allowfullscreen
        ></iframe>
      </div>
      <div class="work-version-hint" v-if="selectedIframeUrls.portal">
        <div style="width: 75%;">
          Die vollständige Wolken-Ansicht mit allen Funktionen finden Sie im Webportal.
        </div>
          <v-btn :href="selectedIframeUrls.portal" target="_blank" color="primary" depressed>
            <v-icon left>mdi-open-in-new</v-icon>
            Zur Work Version
          </v-btn>
      </div>
      </template>
      <template v-else>
        <v-row>
          <v-col cols="12" sm="6" style="display: flex; flex-direction: row; align-items: center;">
            <div>
            <div class="h1 new-font-medium mb-5">Mobile Mapping</div>
            <p>Mit Mobile Mapping treibt die NEW Netz GmbH die digitale Infrastrukturplanung voran. Hochpräzise Straßenraumdaten schaffen die Grundlage für eine effiziente Netzplanung und eine vorausschauende Instandhaltung.</p>
            <v-btn color="primary" depressed @click="scrollToForm">
              Jetz anfragen
            </v-btn>
            </div>
          </v-col>
          <v-col cols="12" sm="6">
            <img :src="mobileMappingHeader" width="100%" />
          </v-col>
        </v-row>
      </template>
      <mobile-mapping-info class="spacing-top" />
      <mobile-mapping-quote class="spacing-top" />
      <mobile-mapping-signup-journey class="spacing-top" v-if="!mobileMappingUrls.iframe" />
      <mobile-mapping-signup-form  class="spacing-top" id="mobile-mapping-signup-form"  v-if="!mobileMappingUrls.iframe" />
      <mobile-mapping-faq  class="spacing-top" />
      <mobile-mapping-contact class="spacing-top" />
    </div>
</div>
</template>

<style scoped>
.work-version-hint {
  background: #D5E1F1 !important;
  width: 100%;
  padding: 12px 16px;
  border-radius: 5px;
  font-size: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.iframe-shell {
  overflow-anchor: none;
}

</style>
